import React, { useState } from "react";
import { Send, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import { sendTelegram, formatBooking } from "../lib/telegram";
import { useLang } from "../contexts/LanguageContext";
import { BRAND } from "../lib/i18n";
import { WhatsAppIcon, TikTokIcon } from "./Header";

const BookingForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: t.booking.services[0],
    message: "",
  });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    const ok = await sendTelegram(formatBooking(form));
    if (ok) {
      setState("success");
      setForm({
        name: "",
        phone: "",
        email: "",
        service: t.booking.services[0],
        message: "",
      });
      setTimeout(() => setState("idle"), 5000);
    } else {
      setState("error");
      setError(t.booking.error);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 relative overflow-hidden"
      data-testid="booking-section"
    >
      <div
        className="absolute -left-40 top-20 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.3) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />

      <div className="container-hd relative z-10">
        <div className="grid lg:grid-cols-[1fr,1.2fr] gap-10 lg:gap-16 items-start">

          <div>
            <div className="font-serif italic text-[color:var(--hd-gold)] text-sm md:text-base mb-3">
              — {t.booking.overline}
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold text-white tracking-tight leading-tight">
              {t.booking.title}
            </h2>
            <p className="text-white/60 mt-5 text-base md:text-lg max-w-xl">
              {t.booking.subtitle}
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-11 h-11 rounded-xl bg-[color:var(--hd-gold)]/10 border border-[color:var(--hd-gold)]/30 flex items-center justify-center group-hover:bg-[color:var(--hd-gold)]/20 transition-colors">
                  <Phone size={18} className="text-[color:var(--hd-gold)]" />
                </div>
                <div>
                  <div className="text-white/40 text-xs uppercase tracking-wider">
                    Phone / WhatsApp
                  </div>
                  <div className="font-display text-white text-lg group-hover:text-[color:var(--hd-gold)] transition-colors">
                    {BRAND.phone}
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-[color:var(--hd-gold)]/10 border border-[color:var(--hd-gold)]/30 flex items-center justify-center">
                  <MapPin size={18} className="text-[color:var(--hd-gold)]" />
                </div>
                <div>
                  <div className="text-white/40 text-xs uppercase tracking-wider">
                    Service Area
                  </div>
                  <div className="font-display text-white text-lg">
                    {BRAND.location}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <a
                  href={BRAND.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-white/70 hover:text-[color:var(--hd-gold)] hover:border-[color:var(--hd-gold)]/50 transition-all"
                  data-testid="contact-instagram"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
                <a
                  href={BRAND.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-white/70 hover:text-[color:var(--hd-gold)] hover:border-[color:var(--hd-gold)]/50 transition-all"
                  data-testid="contact-facebook"
                  aria-label="Facebook"
                >
                  <Facebook size={18} />
                </a>
                <a
                  href={BRAND.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-white/70 hover:text-[color:var(--hd-gold)] hover:border-[color:var(--hd-gold)]/50 transition-all"
                  data-testid="contact-whatsapp"
                  aria-label="WhatsApp"
                >
                  <WhatsAppIcon width={18} height={18} />
                </a>
                <a
                  href={BRAND.tiktok}
                  target="_blank"
                  rel="noreferrer"
                  className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-white/70 hover:text-[color:var(--hd-gold)] hover:border-[color:var(--hd-gold)]/50 transition-all"
                  data-testid="contact-tiktok"
                  aria-label="TikTok"
                >
                  <TikTokIcon width={18} height={18} />
                </a>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="rounded-3xl border border-[color:var(--hd-gold)]/20 bg-[#141414] p-7 md:p-10"
            data-testid="booking-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">
                  {t.booking.fields.name}
                </label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[color:var(--hd-gold)] rounded-xl px-4 py-3 text-white outline-none transition-colors"
                  data-testid="booking-name"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">
                  {t.booking.fields.phone}
                </label>
                <input
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[color:var(--hd-gold)] rounded-xl px-4 py-3 text-white outline-none transition-colors"
                  data-testid="booking-phone"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">
                {t.booking.fields.email}
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[color:var(--hd-gold)] rounded-xl px-4 py-3 text-white outline-none transition-colors"
                data-testid="booking-email"
              />
            </div>

            <div className="mt-4">
              <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">
                {t.booking.fields.service}
              </label>
              <select
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[color:var(--hd-gold)] rounded-xl px-4 py-3 text-white outline-none transition-colors"
                data-testid="booking-service"
              >
                {t.booking.services.map((s, i) => (
                  <option key={i} value={s} className="bg-[#0A0A0A]">
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4">
              <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">
                {t.booking.fields.message}
              </label>
              <textarea
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[color:var(--hd-gold)] rounded-xl px-4 py-3 text-white outline-none transition-colors resize-none"
                data-testid="booking-message"
              />
            </div>

            {state === "success" && (
              <div
                className="mt-4 p-4 rounded-xl border border-[color:var(--hd-gold)]/40 bg-[color:var(--hd-gold)]/10 text-[color:var(--hd-gold)] text-sm"
                data-testid="booking-success"
              >
                ✓ {t.booking.success}
              </div>
            )}
            {state === "error" && (
              <div
                className="mt-4 p-4 rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 text-sm"
                data-testid="booking-error"
              >
                {typeof error === "string" ? error : t.booking.error}
              </div>
            )}

            <button
              type="submit"
              disabled={state === "sending"}
              className="btn-gold w-full justify-center mt-6 disabled:opacity-50"
              data-testid="booking-submit"
            >
              {state === "sending" ? t.booking.sending : t.booking.submit}
              {state !== "sending" && <Send size={14} />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
